import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '@/configs/firebase.config';
import { toast } from 'react-toastify';
import { addMinutes, isAfter, isBefore, parse, set } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { statusType } from '@/configs/constants';

/**
 * Check if a store is available (not closed, active, and within operating hours)
 * @param {string} companyId - The company ID
 * @param {string} storeId - The store ID
 * @param {function} translator - Translation function for error messages
 * @returns {Promise<boolean>} - Returns true if store is available, false if closed
 */
export const checkStoreAvailability = async (companyId, storeId, translator, product = null, userTimezone = null) => {
  if (!companyId || !storeId) {
    return true; // Allow to proceed if no store info
  }
  try {
    // Fetch store document directly from Firestore
    const storeDocRef = doc(firestore, `companies/${companyId}/stores/${storeId}`);
    const storeDoc = await getDoc(storeDocRef);
    if (!storeDoc.exists()) {
      // Store document not found - allow to proceed (validation will handle)
      return true;
    }
    const store = storeDoc.data();

    // Check 1: isClosed field
    if (store.isClosed === true) {
      console.warn('[checkStoreAvailability] unavailable reason: isClosed', {
        companyId,
        storeId,
        isClosed: store.isClosed
      });
      const errorMessage = translator ? translator('store_not_available') : 'Store not available';
      toast.error(errorMessage);
      return false;
    }

    // Check 2: status field (should be 'ACTIVE')
    if (store.status && store.status !== statusType.ACTIVE) {
      console.warn('[checkStoreAvailability] unavailable reason: status', {
        companyId,
        storeId,
        status: store.status,
        expected: statusType.ACTIVE
      });
      const errorMessage = translator ? translator('store_not_available') : 'Store not available';
      toast.error(errorMessage);
      return false;
    }

    // Check 3: Store operating hours (open/close time)
    if (store.open && store.close) {
      const isOpen = checkStoreOperatingHours(store, product, userTimezone);
      if (!isOpen) {
        console.warn('[checkStoreAvailability] unavailable reason: operating_hours', {
          companyId,
          storeId,
          open: store.open,
          close: store.close
        });
        const errorMessage = translator ? translator('store_not_available') : 'Store not available';
        toast.error(errorMessage);
        return false;
      }
    }

    // Store is available
    return true;
  } catch (error) {
    // On error, allow to proceed (better UX than blocking)
    return true;
  }
};

/**
 * Check if store is within operating hours based on timezone
 * @param {Object} store - Store data with open, close, and timeZone fields
 * @param {Object} product - Product object with timeZone field (optional)
 * @param {string} userTimezone - User's selected country timezone (for global delivery)
 * @returns {boolean} - Returns true if store is currently open
 */
const checkStoreOperatingHours = (store, product = null, userTimezone = null) => {
  try {
    // Priority: userTimezone (for global delivery) > product timezone > store timezone
    const timezone = userTimezone || product?.timeZone || product?.company?.timeZone || product?.store?.timeZone || product?.category?.timeZone || product?.companyConfig?.country?.timezone || store?.timeZone || store?.companyConfig?.country?.timezone || 'UTC';
    console.warn('[checkStoreOperatingHours] timezone resolution', {
      userTimezone,
      'product.timeZone': product?.timeZone,
      'product.company.timeZone': product?.company?.timeZone,
      'product.store.timeZone': product?.store?.timeZone,
      'product.category.timeZone': product?.category?.timeZone,
      'product.companyConfig.country.timezone': product?.companyConfig?.country?.timezone,
      'store.timeZone': store?.timeZone,
      'store.companyConfig.country.timezone': store?.companyConfig?.country?.timezone,
      fallback: 'UTC',
      resolvedTimezone: timezone
    });
    // Get current time in store's timezone
    const getCurrentStoreTime = () => {
      return new Date(formatInTimeZone(new Date(), timezone, "yyyy-MM-dd'T'HH:mm:ss.SSS"));
    };

    // Convert store time string to Date object in store's timezone
    const getStoreTime = (timeStr = '') => {
      if (!timeStr) return null;
      try {
        // Remove seconds only if the format is HH:MM:SS (has 3 parts)
        const parts = timeStr.split(':');
        const cleanedTimeStr = parts.length === 3 ? `${parts[0]}:${parts[1]}` : timeStr;
        const timeFormats = ['HH:mm', 'hh:mm a', 'hh:mm'];
        for (const timeFormat of timeFormats) {
          const parsedDate = parse(cleanedTimeStr, timeFormat, new Date());
          if (!Number.isNaN(parsedDate.getTime())) {
            // Use current date in store's timezone
            const storeCurrentTime = getCurrentStoreTime();
            const dateStr = formatInTimeZone(storeCurrentTime, timezone, 'yyyy-MM-dd');

            // Create proper date with timezone
            const hours = parsedDate.getHours();
            const minutes = parsedDate.getMinutes();
            const result = set(new Date(`${dateStr}T00:00:00`), {
              hours: hours,
              minutes: minutes,
              seconds: 0,
              milliseconds: 0
            });
            return result;
          }
        }
        return null;
      } catch (error) {
        return null;
      }
    };
    const openTime = getStoreTime(store.open);
    let closeTime = getStoreTime(store.close);
    const storeCurrentTime = getCurrentStoreTime();
    if (!openTime || !closeTime) {
      // If we can't parse times, assume store is open
      return true;
    }

    // If closing time is before or equal to opening time, assume it's next day (overnight hours or 24-hour operation)
    // Example: open: "00:00:00", close: "00:00" (24 hours) or open: "22:00:00", close: "02:00" (overnight)
    if (isBefore(closeTime, openTime) || closeTime.getTime() === openTime.getTime()) {
      closeTime = addMinutes(closeTime, 24 * 60); // Add 24 hours
    }

    // Check if current time is between open and close time
    const isOpen = isAfter(storeCurrentTime, openTime) && isAfter(closeTime, storeCurrentTime);
    console.warn('[checkStoreOperatingHours] window evaluation', {
      timezone,
      open: store.open,
      close: store.close,
      storeCurrentTime: storeCurrentTime.toISOString(),
      openTime: openTime.toISOString(),
      closeTime: closeTime.toISOString(),
      isOpen
    });
    return isOpen;
  } catch (error) {
    // On error, assume store is open (better UX)
    return true;
  }
};

/**
 * Hook to use store availability check
 */
export const useStoreAvailability = () => {
  return {
    checkStoreAvailability
  };
};